import React from 'react';
import { Settings, Plus, Pencil, Trash2, Loader2, ClipboardList } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { moduleApi } from '../../lib/api';
import { BriefQuestion } from './BriefQuestionsForm';
import { BriefTemplateEditor } from './BriefTemplateEditor';
import { useConfirm } from '../ui/ConfirmProvider';

interface BriefTemplate { id: string; name: string; questions: BriefQuestion[]; is_active: boolean; updated_at?: string }

/**
 * Bouton Paramètres du module Suivi privatisation : configure les modèles de Brief. Volontairement à part
 * de Paramètres > Démarches (composant DocumentTemplatesSettings) — un modèle de Brief a sa propre table
 * (privatisation_brief_templates) et n'apparaît donc jamais dans le sélecteur « Démarche à envoyer » de la
 * plaquette ou du devis (demande d'Emeni, 2026-09-01 : les deux ne doivent jamais se mélanger).
 */
export function BriefTemplatesModal({ onClose }: { onClose: () => void }) {
    const [chargement, setChargement] = React.useState(true);
    const confirmAction = useConfirm();
    const [modeles, setModeles] = React.useState<BriefTemplate[]>([]);
    const [erreur, setErreur] = React.useState('');
    const [edition, setEdition] = React.useState<BriefTemplate | 'nouveau' | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [erreurEdition, setErreurEdition] = React.useState('');

    const charger = React.useCallback(() => {
        setChargement(true);
        moduleApi.getBriefTemplates()
            .then((r: any) => setModeles(Array.isArray(r) ? r : []))
            .catch((e: any) => setErreur(e?.message || 'Chargement impossible.'))
            .finally(() => setChargement(false));
    }, []);
    React.useEffect(() => { charger(); }, [charger]);

    const enregistrer = async (payload: { name: string; questions: BriefQuestion[]; is_active: boolean }) => {
        setSaving(true);
        setErreurEdition('');
        try {
            if (edition && edition !== 'nouveau') await moduleApi.updateBriefTemplate(edition.id, payload);
            else await moduleApi.createBriefTemplate(payload);
            setEdition(null);
            charger();
        } catch (e: any) {
            setErreurEdition(e?.message || "L'enregistrement a échoué.");
        } finally {
            setSaving(false);
        }
    };

    const supprimer = async (t: BriefTemplate) => {
        if (!(await confirmAction({ title: 'Supprimer le modèle de brief ?', description: <>« {t.name} » sera supprimé ; les dossiers qui l'utilisent repasseront sur « Aucun choisi ».</>, confirmLabel: 'Supprimer le modèle' }))) return;
        try {
            await moduleApi.deleteBriefTemplate(t.id);
            charger();
        } catch (e: any) {
            setErreur(e?.message || 'Suppression impossible.');
        }
    };

    if (edition) {
        return (
            <Modal open onClose={() => setEdition(null)} title={edition === 'nouveau' ? 'Nouveau modèle de brief' : edition.name} icon={<ClipboardList size={16} />} maxWidth="max-w-lg">
                <BriefTemplateEditor
                    initial={edition === 'nouveau' ? null : edition}
                    saving={saving}
                    error={erreurEdition}
                    onCancel={() => setEdition(null)}
                    onSave={enregistrer}
                />
            </Modal>
        );
    }

    return (
        <Modal open onClose={onClose} title="Modèles de brief" icon={<Settings size={16} />} maxWidth="max-w-lg">
            <div className="space-y-3">
                <p className="text-[11px] text-content-muted leading-relaxed">
                    Les questions que vos commerciales posent pour établir un devis — indépendantes des Démarches
                    envoyées au client. Chaque dossier choisit son modèle dans l'onglet Brief.
                </p>

                <button onClick={() => setEdition('nouveau')} type="button"
                    className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold">
                    <Plus size={13} /> Nouveau modèle
                </button>

                {chargement ? (
                    <div className="flex justify-center py-6"><Loader2 className="animate-spin text-content-muted" size={20} /></div>
                ) : erreur ? (
                    <p className="text-xs text-red-500 font-semibold">{erreur}</p>
                ) : modeles.length === 0 ? (
                    <p className="text-[11px] text-content-muted text-center py-4">Aucun modèle de brief pour l'instant.</p>
                ) : (
                    <div className="space-y-1.5">
                        {modeles.map((t) => (
                            <div key={t.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-border bg-surface-soft">
                                <div className="min-w-0">
                                    <p className="text-xs font-bold text-content truncate">{t.name}</p>
                                    <p className="text-[10px] text-content-muted">
                                        {t.questions.length} question{t.questions.length > 1 ? 's' : ''}
                                        {!t.is_active && ' · inactif'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => setEdition(t)} type="button" className="p-1.5 text-content-muted hover:text-content rounded-md hover:bg-surface"><Pencil size={13} /></button>
                                    <button onClick={() => supprimer(t)} type="button" className="p-1.5 text-red-400 hover:text-red-500 rounded-md hover:bg-surface"><Trash2 size={13} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
}
